import Axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL || '';

const generateRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const AXIOS_INSTANCE = Axios.create({
  baseURL: API_BASE_URL,
  timeout: 180000, // 3 minutes (conversions query can be slow)
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true, // Required for @leap4/auth cookie-based auth
});

const getAuthToken = (): string | null => {

  try {
    const token = localStorage.getItem('accessToken');
    if (token) return token;

    const authData = localStorage.getItem('auth');
    if (authData) {
      const parsed = JSON.parse(authData);
      if (parsed.accessToken) return parsed.accessToken;
    }
  } catch {

  }
  return null;
};

AXIOS_INSTANCE.interceptors.request.use(
  (config) => {

    const token = getAuthToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const organization = localStorage.getItem('x-organization');
    if (organization) {
      config.headers['x-organization'] = organization;
    }

    config.headers['X-Request-ID'] = generateRequestId();

    return config;
  },
  (error) => Promise.reject(error)
);

AXIOS_INSTANCE.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<ErrorResponse>) => {
    const status = error.response?.status;

    if (status === 401) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: error }));
    }

    if (status === 403) {
      window.dispatchEvent(new CustomEvent('auth:forbidden', { detail: error }));
    }

    if (status === 429) {
      const retryAfter = error.response?.headers['retry-after'];
      console.warn(`Rate limited. Retry after: ${retryAfter}s`);
    }

    return Promise.reject(error);
  }
);

export interface ErrorResponse {
  statusCode: number;
  message: string | string[];
  error: string;
  timestamp?: string;
  path?: string;
}

export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: { signal?: AbortSignal }
): Promise<T> => {
  const controller = new AbortController();

  const promise = AXIOS_INSTANCE({
    ...config,
    signal: options?.signal ?? controller.signal,
  }).then(({ data }) => data as T);

  // @ts-expect-error - Adding cancel for React Query compatibility
  promise.cancel = () => controller.abort();

  return promise;
};

export type ErrorType<TError = ErrorResponse> = AxiosError<TError>;
export type BodyType<TBody> = TBody;

export default customInstance;
